import { ReactNode, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { Draggable, Droppable } from 'react-beautiful-dnd'
import { GripVertical, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'

/**
 * Pure array reorder shared by every `DraggableFieldArray` consumer's
 * `onDragEnd`. `DraggableFieldArray` never owns a `DragDropContext` itself —
 * react-beautiful-dnd doesn't support nesting them, so a container with a
 * nested list (e.g. Footer's columns, each holding its own independent list
 * of links) must render ONE context covering every `Droppable` on the page
 * and route `result.source.droppableId` to the right array/path itself,
 * calling this helper to compute the reordered array.
 */
export function reorderFieldArray<T>(
  items: T[],
  sourceIndex: number,
  destinationIndex: number
): T[] {
  const next = [...items]
  const [moved] = next.splice(sourceIndex, 1)
  next.splice(destinationIndex, 0, moved)
  return next
}

export interface DraggableFieldArrayProps<T = any> {
  /**
   * The owning react-hook-form instance. Array mutations go through
   * `getValues`/`setValue` — this codebase's established convention for
   * array fields that aren't wired through `useFieldArray` (see
   * `ContentBlockFormBody`'s `selected_page_ids` drag-reorder) — rather than
   * `useFieldArray`, so the caller's `renderItem` can freely address nested
   * paths like `items.${index}.profiles.${languageId}.label` the same way
   * the rest of this codebase's forms do.
   */
  form: UseFormReturn<any>
  /** Dot path of the array field, e.g. `items` or `columns.0.links`. */
  name: string
  /**
   * Must be unique among every `Droppable` sharing the container's
   * `DragDropContext` (e.g. `footer-links-2`) — the container's `onDragEnd`
   * routes on this id, see `reorderFieldArray`'s doc.
   */
  droppableId: string
  /** Renders the expanded body for the item at `index`. Closes over the caller's own `control`/`form`. */
  renderItem: (index: number) => ReactNode
  /**
   * Factory for a new item, called on "Add". MUST set a stable `_key` (e.g.
   * `crypto.randomUUID()`) on the returned item — used as the React/RBD key
   * so an edit, reorder, or removal can't hand one row's uncontrolled
   * input/focus state to whichever row now occupies its old index (this
   * array is read via `form.watch`, which re-renders the whole list on
   * every keystroke in any row). `_key` is never sent to the server —
   * submit payloads are built from named fields, never a spread of the row.
   */
  onAdd: () => T
  /** Collapsed-row label. */
  itemTitle: (item: T, index: number) => string
  addLabel?: string
  emptyText?: string
}

export function DraggableFieldArray<T = any>({
  form,
  name,
  droppableId,
  renderItem,
  onAdd,
  itemTitle,
  addLabel = 'Add item',
  emptyText = 'No items yet.',
}: DraggableFieldArrayProps<T>) {
  const items: T[] = form.watch(name) ?? []
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  const getKey = (item: T, index: number): string =>
    (item as any)?._key ?? `${droppableId}-${index}`

  const handleAdd = () => {
    const current = [...(form.getValues(name) ?? [])]
    const item = onAdd()
    current.push(item)
    form.setValue(name, current, { shouldDirty: true })
    setExpandedKey(getKey(item, current.length - 1))
  }

  const handleRemove = (index: number) => {
    const current = [...(form.getValues(name) ?? [])]
    current.splice(index, 1)
    form.setValue(name, current, { shouldDirty: true })
  }

  // Forces a clean remount of the list whenever the SET or ORDER of rows
  // changes (add / remove / drag) — never on an in-place edit to one row's
  // fields, since editing doesn't change any row's key. Without this, a
  // reorder/removal can leave a row's uncontrolled DOM state (focus, native
  // input value) attached to whichever row now sits at that index.
  const orderSignature = items.map((item, index) => getKey(item, index)).join('|')

  return (
    <div className="space-y-2">
      {items.length === 0 && (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      )}
      <Droppable droppableId={droppableId}>
        {provided => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            key={orderSignature}
            className="space-y-2"
          >
            {items.map((item, index) => {
              const key = getKey(item, index)
              const expanded = expandedKey === key
              return (
                <Draggable key={key} draggableId={key} index={index}>
                  {dragProvided => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      className="rounded-lg border bg-white"
                    >
                      <div className="flex items-center gap-2 px-3 py-2">
                        <div
                          {...dragProvided.dragHandleProps}
                          className="cursor-grab text-muted-foreground shrink-0"
                        >
                          <GripVertical className="h-4 w-4" />
                        </div>
                        <button
                          type="button"
                          className="flex-1 text-left text-sm font-medium truncate"
                          onClick={() => setExpandedKey(expanded ? null : key)}
                        >
                          {itemTitle(item, index)}
                        </button>
                        {expanded ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0 text-red-500 hover:text-red-700"
                          onClick={() => handleRemove(index)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      {expanded && (
                        <div className="border-t px-3 py-3 space-y-4">
                          {renderItem(index)}
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              )
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      <Button type="button" variant="outline" size="sm" onClick={handleAdd}>
        <Plus className="h-4 w-4 mr-2" />
        {addLabel}
      </Button>
    </div>
  )
}
